import React from "react";
import { cn } from "@/lib/utils";
import { PenLine, Shield, Calendar } from "lucide-react";

interface SignaturePanelProps {
  customerName?: string;
  date?: string;
  privacyAccepted?: boolean;
  signatureUrl?: string;
  animated?: boolean;
  className?: string;
}

export function SignaturePanel({
  customerName = "",
  date,
  privacyAccepted = false,
  signatureUrl,
  animated = true,
  className,
}: SignaturePanelProps) {
  const currentDate = date || new Date().toLocaleDateString('de-AT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  return (
    <div className={cn("space-y-6", className)}>
      <div className="text-center">
        <h3 className="font-semibold text-lg">Beratungsprotokoll</h3>
        <p className="text-sm text-muted-foreground">
          Zusammenfassung und Bestätigung
        </p>
      </div>

      {/* Info Cards */}
      <div className={cn(
        "grid grid-cols-2 gap-4",
        animated && "animate-in fade-in slide-in-from-bottom-4"
      )}>
        <div className="bg-muted/50 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="h-4 w-4 text-primary" />
            <span className="text-xs text-muted-foreground">Datum</span>
          </div>
          <span className="font-semibold">{currentDate}</span>
        </div>
        <div className="bg-muted/50 p-4 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <Shield className="h-4 w-4 text-primary" />
            <span className="text-xs text-muted-foreground">Datenschutz</span>
          </div>
          <span className={cn(
            "font-semibold",
            privacyAccepted ? "text-emerald-600" : "text-muted-foreground"
          )}>
            {privacyAccepted ? "Akzeptiert" : "Ausstehend"}
          </span>
        </div>
      </div>

      {/* Signature Area */}
      <div className={cn(
        "border-2 border-dashed border-muted-foreground/30 rounded-xl p-6",
        animated && "animate-in fade-in"
      )}
      style={animated ? { animationDelay: '200ms' } : undefined}
      >
        <div className="flex items-center justify-center gap-2 mb-4 text-muted-foreground">
          <PenLine className="h-5 w-5" />
          <span className="text-sm font-medium">Unterschrift Kunde</span>
        </div>

        {signatureUrl ? (
          <img
            src={signatureUrl}
            alt="Unterschrift"
            className="max-h-24 mx-auto"
          />
        ) : (
          <div className="h-24 flex items-center justify-center">
            <span className="text-muted-foreground/50 italic">
              {customerName || "Unterschrift ausstehend"}
            </span>
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-muted-foreground/20 text-center">
          <p className="text-sm text-muted-foreground">{customerName}</p>
        </div>
      </div>

      {/* Privacy Note */}
      <div className="bg-muted/30 p-4 rounded-lg">
        <p className="text-xs text-muted-foreground text-center leading-relaxed">
          Mit Ihrer Unterschrift bestätigen Sie, dass Sie die Beratung erhalten haben
          und mit der Verarbeitung Ihrer Daten gemäß DSGVO einverstanden sind.
          Eine Kopie des Protokolls wird Ihnen per E-Mail zugesendet.
        </p>
      </div>
    </div>
  );
}
